import * as React from "react";
import { connect } from "react-redux";
import { StoreState } from "../../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles, FormControlLabel } from "@material-ui/core";
import { translate, InjectedTranslateProps } from "react-i18next";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import DialogContentText from "@material-ui/core/DialogContentText";
import { Payment } from '../../api/payments';
import { ApiError } from '../../api/dispatcher';
import { setSnackbarMessage } from '../../actions/store/snackbar-actions';
import { SnackbarVariant } from '../../actions/store/type';
import { Dispatch } from 'redux';
import { closeAddPaymentDialog } from '../../actions/store/app-actions';
import { refreshInquiry, updateInquiry } from '../../actions/inquiry-actions';
import { addHours } from 'date-fns';
import { formatDistanceStrictToNow } from '../../util/date-utility';
import { conf } from '../../conf';
import { addPaymentAndUpdateStore, updatePaymentAndUpdateStore } from '../../actions/payment-actions';

// Description of the credit card deposit at mollie (the server uses the same
// fixed text; the text of a no-show charge is entered when charging)
const GUARANTEE_DESCRIPTION = "No-Show Tischreservierung Xandl Stadl";

interface State {
	formDisabled: boolean;
	dialogAmount: string;
	dialogName: string;
	dialogEmail: string;
	dialogValidHours: number;
	dialogMessage?: string;
	dialogReservationDate: string;
	dialogConvertToReservation: boolean;
}

export interface AddPaymentDialogProps {}

type Props = AddPaymentDialogProps &
	ReturnType<typeof mapStateToProps> &
	ReturnType<typeof mapDispatchToProps> &
	WithStyles<typeof styles> &
	InjectedTranslateProps;

export class AddPaymentDialog extends React.Component<Props, {}> {
	public readonly state: State = {
		formDisabled: false,
		dialogAmount: this.props.passedAmount ? this.props.passedAmount : conf.payments.defaultAmount.toFixed(2),
		dialogName: this.props.passedName ? this.props.passedName : "",
		dialogEmail: this.props.passedEmail ? this.props.passedEmail : "",
		dialogValidHours: this.props.passedValidHours ? this.props.passedValidHours : conf.payments.defaultValidHours,
		dialogMessage: this.props.passedMessage ? this.props.passedMessage : "",
		dialogReservationDate: "",
		dialogConvertToReservation: false
	};

	private form: HTMLFormElement;

	private handleFormRef = (ref: HTMLFormElement) => {
		this.form = ref;
	}

	public componentWillReceiveProps(nextProps: Props) {
		if (this.props.open === false && nextProps.open === true) {
			this.setStateFromProps(nextProps);
		}
	}

	private setStateFromProps = (props: Props) => {
		this.setState({
			formDisabled: false,
			dialogAmount: props.passedAmount ? props.passedAmount : conf.payments.defaultAmount.toFixed(2),
			dialogName: props.passedName ? props.passedName : "",
			dialogEmail: props.passedEmail ? props.passedEmail : "",
			dialogValidHours: props.passedValidHours ? props.passedValidHours : conf.payments.defaultValidHours,
			dialogMessage: props.passedMessage ? props.passedMessage : "",
			dialogReservationDate: "",
			dialogConvertToReservation: false
		});
	};

	private handleDialogAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const dialogAmount = parseFloat(event.currentTarget.value);

		this.setState({
			dialogAmount: isNaN(dialogAmount) ? "" : dialogAmount
		});
	};

	private handleDialogAmountBlur = () => {
		const { dialogAmount } = this.state;

		const roundedAmount =
			dialogAmount === ""
				? "0"
				: parseFloat(dialogAmount).toFixed(2);

		this.setState({ dialogAmount: roundedAmount });
	};

	private handleDialogEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ dialogEmail: event.currentTarget.value });
	};

	private handleDialogValidHoursChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ dialogValidHours: event.currentTarget.value });
	};

	private handleDialogNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ dialogName: event.currentTarget.value });
	};

	private handleDialogMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ dialogMessage: event.currentTarget.value });
	};

	private handleDialogReservationDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({ dialogReservationDate: event.currentTarget.value });
	};

	// Generic card guarantees (neither inquiry nor reservation) need the date of the visit
	private needsReservationDate = () => {
		const { inquiryId, paymentId, reservationId } = this.props;

		return !Boolean(inquiryId) && !Boolean(paymentId) && !Boolean(reservationId);
	};

	// Requested for an existing reservation (occupancy plan)
	private isForReservation = () => {
		const { inquiryId, reservationId } = this.props;

		return !Boolean(inquiryId) && Boolean(reservationId);
	};

	private handleDialogClose = () => {
		this.props.close();
	};

	private handleDialogSubmitClick = async () => {
		const { formDisabled, dialogAmount, dialogName, dialogEmail, dialogValidHours, dialogMessage, dialogReservationDate, dialogConvertToReservation } = this.state;
		const { inquiryId, paymentId } = this.props;

		if (formDisabled) return;
		if (!this.form.reportValidity()) return;

		this.setState({ formDisabled: true })

		const amount = parseFloat(dialogAmount);
		const isInquiry = inquiryId && inquiryId !== "";

		const payload: Payment = {
			amount,
			currency: 'EUR',
			name: dialogName,
			items: [{
				amount,
				name: GUARANTEE_DESCRIPTION
			}],
			transactionName: GUARANTEE_DESCRIPTION,
			expirationDate: addHours(new Date(), dialogValidHours).toISOString(),
			email: dialogEmail,
			type: "guarantee"
		};

		if (this.needsReservationDate() && dialogReservationDate) {
			const [year, month, day] = dialogReservationDate.split("-").map(Number);
			payload.reservationDate = new Date(year, month - 1, day).toISOString();
		}

		if (isInquiry) {
			payload.inquiryId = inquiryId;
		}

		if (this.isForReservation() && !Boolean(paymentId)) {
			payload.reservationId = this.props.reservationId;
		}

		if (Boolean(dialogMessage)) {
			payload.message = dialogMessage;
		}

		try {
			if (paymentId && paymentId !== "") {
				await updatePaymentAndUpdateStore(paymentId, payload);
			} else {
				await addPaymentAndUpdateStore(payload);
			}
		} catch (e) {
			console.error(e);

			const error = e as ApiError;
			this.props.setSnackbarMessage(error.serverMessage || "Die Anforderung konnte nicht gesendet werden!", "error");
			this.setState({ formDisabled: false });

			return;
		}

		// The request has been sent: a failing follow-up must not lead to a resubmit
		if (isInquiry) {
			try {
				if (dialogConvertToReservation == true && !Boolean(paymentId)) {
					await updateInquiry(inquiryId, {
						moved: true
					});
				} else {
					await refreshInquiry(inquiryId);
				}
			} catch (e) {
				console.error(e);
				this.props.setSnackbarMessage("Anforderung gesendet, die Anfrage konnte aber nicht aktualisiert werden.", "warning");
			}
		}

		this.handleDialogClose();
	}

	private generateDurationHelperText = () => {
		const { dialogValidHours } = this.state;

		if (dialogValidHours < 24) return;

		let prefix = "";

		if (dialogValidHours % 24 !== 0) {
			prefix = "~ " + prefix;
		}

		return prefix + formatDistanceStrictToNow(addHours(new Date(), dialogValidHours));
	};

	public render() {
		const {
			classes,
			open,
			inquiryId,
			paymentId
		} = this.props;
		const {
			formDisabled,
			dialogAmount,
			dialogName,
			dialogEmail,
			dialogValidHours,
			dialogMessage,
			dialogReservationDate,
			dialogConvertToReservation
		} = this.state;

		return (
			<Dialog
				open={open}
				onClose={this.handleDialogClose}
			>
				<DialogTitle>
					{Boolean(paymentId) ? "Link zur Kartenhinterlegung erneut senden" : "Kreditkarte hinterlegen lassen"}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Der Gast hinterlegt eine Kreditkarte als Garantie, dabei wird nichts abgebucht.
						Nur wenn der Gast nicht erscheint, kann die No-Show-Gebühr
						bis {conf.payments.noShowChargeDays} Tage nach dem Reservierungsdatum abgebucht werden.
					</DialogContentText>
					<form ref={this.handleFormRef}>
						<div className={classes.formRow}>
							<TextField
								autoFocus
								margin="dense"
								type="number"
								required
								label="No-Show-Gebühr"
								className={classes.textField}
								value={isNaN(parseFloat(dialogAmount)) ? "" : dialogAmount}
								disabled={formDisabled}
								onChange={this.handleDialogAmountChange}
								onBlur={this.handleDialogAmountBlur}
								InputProps={{
									startAdornment: <InputAdornment position="start">€</InputAdornment>
								}}
								inputProps={{
									min: "1",
									step: "0.01"
								}}
							/>
							<TextField
								margin="dense"
								type="number"
								value={dialogValidHours}
								required
								label="Link gültig"
								helperText={this.generateDurationHelperText()}
								className={classes.textField}
								disabled={formDisabled}
								onChange={this.handleDialogValidHoursChange}
								InputProps={{
									endAdornment: <InputAdornment position="end">Stunden</InputAdornment>
								}}
								inputProps={{
									min: 12,
									max: 336
								}}
							/>
						</div>
						{this.needsReservationDate() && (
							<TextField
								margin="dense"
								type="date"
								value={dialogReservationDate}
								fullWidth
								required
								label="Reservierungsdatum"
								disabled={formDisabled}
								onChange={this.handleDialogReservationDateChange}
								InputLabelProps={{
									shrink: true
								}}
							/>
						)}
						<div className={classes.formRow}>
							<TextField
								margin="dense"
								type="text"
								value={dialogName}
								required
								label="Name"
								className={classes.textField}
								disabled={formDisabled}
								onChange={this.handleDialogNameChange}
							/>
							<TextField
								margin="dense"
								type="email"
								value={dialogEmail}
								required
								label="E-Mail Adresse"
								className={classes.textField}
								disabled={formDisabled}
								onChange={this.handleDialogEmailChange}
							/>
						</div>
						{/* The message is part of the email for generic and reservation requests (not for inquiries) */}
						{!Boolean(inquiryId) && (
							<TextField
								margin="dense"
								type="text"
								value={dialogMessage}
								fullWidth
								required={!this.isForReservation()}
								label={this.isForReservation() ? "Nachricht (optional)" : "Nachricht"}
								multiline={true}
								disabled={formDisabled}
								rows={3}
								onChange={this.handleDialogMessageChange}
								/>
						)}
						{Boolean(inquiryId) && !Boolean(paymentId) && (
							<FormControlLabel
								className={classes.checkbox}
								control={
									<Checkbox
										id="checkedIn"
										color="primary"
										checked={dialogConvertToReservation}
										disabled={formDisabled}
										onChange={event =>
											this.setState({
												dialogConvertToReservation: event.target.checked
											})
										}
									/>
								}
								label="Anfrage entfernen und zu den Reservierungen verschieben"
							/>
						)}
					</form>
				</DialogContent>
				<DialogActions>
					<Button
						className="close-add-payment-dialog-button"
						disabled={formDisabled}
						onClick={this.handleDialogClose}
					>
						Abbrechen
					</Button>
					<Button
						className="submit-payment-button"
						disabled={formDisabled}
						onClick={this.handleDialogSubmitClick}
						color="primary"
					>
						Senden
					</Button>
				</DialogActions>
			</Dialog>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	open: state.addPaymentDialogOpen,
	inquiryId: state.addPaymentDialogInquiryId,
	paymentId: state.addPaymentDialogPaymentId,
	passedEmail: state.addPaymentDialogEmail,
	passedName: state.addPaymentDialogName,
	passedAmount: state.addPaymentDialogAmount,
	passedValidHours: state.addPaymentDialogValidHours,
	passedMessage: state.addPaymentDialogMessage,
	reservationId: state.addPaymentDialogReservationId
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	close: () => dispatch(closeAddPaymentDialog()),
	setSnackbarMessage: (message: string, variant?: SnackbarVariant) =>
		dispatch(setSnackbarMessage(message, variant))
});

const styles = (theme: Theme) =>
	createStyles({
		formRow: {
			display: "flex",
			// Only horizontal: aligns the fields with the dialog edges, a negative
			// vertical margin would pull the row into the text or field above
			margin: "0 -10px",

			"@media (max-width: 600px)": {
				display: "block"
			}
		},
		textField: {
			margin: 10,
			width: "calc(50% - 20px)",

			"@media (max-width: 600px)": {
				width: "calc(100% - 20px)"
			}
		},
		checkbox: {
			marginTop: 8,
			userSelect: "none",
			containerType: "inline-size",
			width: "100%",
			"& span": {  
				lineHeight: 1.2,
				paddingLeft: 5
			}
		},
	}); 

export const ConnectedAddPaymentDialog = translate("translations")(
	withStyles(styles)(connect(mapStateToProps, mapDispatchToProps)(AddPaymentDialog))
);
